import { useEffect, useState } from "react";
import { Target, CheckCircle2, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const FALLBACK_TITLES = [
  "Launch our first youth mentorship cohort with trained, background-checked mentors",
  "Open after-school academic enrichment and tutoring sessions",
  "Run seasonal athletic development and conditioning camps",
  "Host health & wellness clinics for youth and families",
  "Organize our first college visit and career exposure tour",
  "Build verified partnerships with local schools and community organizations",
];

export const ImpactGoals = () => {
  const [goals, setGoals] = useState(
    FALLBACK_TITLES.map((title, i) => ({ slug: `g${i}`, title, status: "planned", completed_at: null }))
  );

  useEffect(() => {
    api
      .get("/goals")
      .then(({ data }) => {
        if (Array.isArray(data) && data.length) setGoals(data);
      })
      .catch(() => {});
  }, []);

  const doneCount = goals.filter((g) => g.status === "completed").length;

  return (
    <section id="impact" className="py-20 sm:py-28 lg:py-36 px-4 sm:px-8 lg:px-16" data-testid="impact-section">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <Reveal>
          <div className="font-mono text-xs sm:text-sm tracking-[0.3em] uppercase text-[#FFB800] mb-4">Impact & Goals</div>
          <h2 className="font-display font-black uppercase tracking-tight leading-none text-3xl sm:text-4xl lg:text-6xl text-[#F4F4F6]">
            We publish goals. <span className="text-outline">Not inflated numbers.</span>
          </h2>
          <p className="mt-8 text-sm sm:text-base leading-relaxed text-[#A1A1B3]">
            G.A.M.E. Mentorship and Training Inc. is a growing youth organization. Instead of
            publishing unverified statistics, we share exactly where we're headed — and update
            this page with completed, verified outcomes as they happen.
          </p>
          <div className="mt-8 flex items-center gap-4 border border-[#242436] bg-[#111118] p-5" data-testid="impact-progress-box">
            {doneCount > 0 ? (
              <>
                <CheckCircle2 className="w-8 h-8 text-[#FFB800] shrink-0" strokeWidth={1.75} />
                <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#A1A1B3] leading-relaxed">
                  {doneCount} of {goals.length} goals completed — verified outcomes will keep appearing here.
                </p>
              </>
            ) : (
              <>
                <TrendingUp className="w-8 h-8 text-[#FFB800] shrink-0" strokeWidth={1.75} />
                <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#A1A1B3] leading-relaxed">
                  Completed outcomes will appear here once achieved and verified by the organization.
                </p>
              </>
            )}
          </div>
        </Reveal>
        <div className="space-y-4">
          {goals.map((g, i) => (
            <Reveal key={g.slug} delay={i * 0.06}>
              <div
                className={`flex items-start gap-5 border bg-[#111118] p-6 transition-colors duration-300 ${
                  g.status === "completed" ? "border-[#FFB800]/50" : "border-[#242436] hover:border-[#8B5CF6]/50"
                }`}
                data-testid={`goal-card-${i}`}
              >
                <span className="font-mono text-sm tracking-[0.3em] text-[#FFB800] shrink-0 pt-1">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  {g.status === "completed" ? (
                    <span className="inline-flex items-center gap-1.5 bg-[#FFB800]/15 text-[#FFB800] font-mono text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 mb-2" data-testid={`goal-badge-completed-${i}`}>
                      <CheckCircle2 className="w-3 h-3" />
                      Completed{g.completed_at ? ` · ${new Date(g.completed_at).toLocaleDateString()}` : ""}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 bg-[#8B5CF6]/15 text-[#C4B5FD] font-mono text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 mb-2">
                      <Target className="w-3 h-3" /> Goal
                    </span>
                  )}
                  <p className="text-sm sm:text-base leading-relaxed text-[#F4F4F6]">{g.title}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
